/**
 * ===== TIME STORE - STORE ZUSTAND GESTION DU TEMPS =====
 * 
 * Store principal pour la gestion du temps de travail avec pointage, statistiques et rapports.
 * Utilise Zustand pour le state management et inclut un système de cache basique.
 * 
 * FONCTIONNALITÉS PRINCIPALES :
 * - Gestion du pointage (arrivée/départ/pauses)
 * - Récupération des entrées du jour et historique
 * - Calculs statistiques (weekly/monthly)
 * - Rapports mensuels et d'équipe
 * - Cache simple pour éviter les recalculs
 * - Fallback en cas d'erreur API
 * 
 * ACTIONS DISPONIBLES :
 * - fetchTodayEntries : Récupère les pointages du jour
 * - fetchTimeHistory : Historique des temps de travail
 * - clockIn/clockOut : Pointage arrivée/départ
 * - startBreak/endBreak : Gestion des pauses
 * - fetchMonthlyReport : Rapports mensuels détaillés
 * - fetchTeamSummary : Synthèse d'équipe pour managers
 * 
 * PROBLÈMES IDENTIFIÉS :
 * - Console.warn et console.log à supprimer en production
 * - Cache très basique (pourrait utiliser une lib comme React Query)
 * - Gestion d'erreurs parfois incohérente
 * - Calculs statistiques répétés dans plusieurs stores
 * 
 * AMÉLIORATIONS SUGGÉRÉES :
 * - Centraliser la gestion d'erreurs
 * - Implémenter un cache plus robuste
 * - Externaliser les calculs statistiques
 * - Ajouter de la persistence locale pour mode offline
 */

// Store pour le suivi du temps
import { create } from 'zustand';
import api from '../services/api';
import { calculateWeeklyStats, 
  calculateMonthlyStats,
  calculateTotalHours, 
  getTodayStatus,
} from '../utils/timeCalculations';

export const useTimeStore = create((set, get) => ({
  todayEntries: [],
  timeHistory: [],
  processedHistory: [], 
  monthlyReport: {},  
  stats: {},
  weeklyStats: {},
  monthlyStats: {},
  loading: false,
  error: null,
  lastUpdate: null,
  teamSummary: [],
  teamLoading: false,
    // Cache simple et efficace
  _processedCache: null,
  _cacheKey: null,
  _cacheTime: 0,

  // Actions principales avec /today
  fetchTodayEntries: async (userId = null) => {
    set({ loading: true, error: null });
    
    try {
      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);
      
      const response = await api.get(`/time-tracking/today?${params}`);
      
      if (response.data.success) {
        set({ 
          todayEntries: response.data.data || [], 
          loading: false,
          lastUpdate: new Date().toISOString()
        });
        return { success: true, data: response.data.data };
      } else {
        throw new Error(response.data.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.warn('API today non disponible, fallback...', error.response?.status);
      
      // FALLBACK
      if (error.response?.status === 404) {
        return get().fetchTodayEntriesLegacy(userId);
      }
      
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      return { success: false, error: errorMessage };
    }
  },

  // fallback
  fetchTodayEntriesLegacy: async (userId = null) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const params = new URLSearchParams({
        startDate: today,
        endDate: today
      });
      
      if (userId) params.append('userId', userId);
      
      const response = await api.get(`/time-tracking/range?${params}`);
      
      if (response.data.success) {
        set({ 
          todayEntries: response.data.data || [], 
          loading: false,
          lastUpdate: new Date().toISOString()
        });
        return { success: true, data: response.data.data };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      return { success: false, error: errorMessage };
    }
  },

  // Actions rapides optimisées
  clockIn: async (taskId = null, comment = '') => {
    return get().performAction('/time-tracking/clock-in', 'arrival', { taskId, comment });
  },

  clockOut: async (comment = '') => {
    return get().performAction('/time-tracking/clock-out', 'departure', { comment });
  },

  startBreak: async (comment = '') => {
    return get().performAction('/time-tracking/break-start', 'break_start', { comment });
  },

  endBreak: async (comment = '') => {
    return get().performAction('/time-tracking/break-end', 'break_end', { comment });
  },

  // utilitaire pour éviter la duplication
  performAction: async (endpoint, fallbackType, { taskId = null, comment = '' }) => {
    set({ error: null });
    
    try {
      const payload = {};
      if (taskId) payload.task_id = taskId;
      if (comment) payload.comment = comment.trim();

      const response = await api.post(endpoint, payload);
      
      if (response.data.success) {
        await get().fetchTodayEntries();
        return { success: true, data: response.data.data };
      } else {
        throw new Error(response.data.message || 'Erreur lors du pointage');
      }
    } catch (error) {
      // FALLBACK vers méthode générique
      if (error.response?.status === 404) {
        return get().recordTimeEntry(fallbackType, taskId, comment);
      }
      
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  // Stats avec fallback
  fetchStats: async (days = 7, userId = null) => {
    set({ loading: true, error: null });
    
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (userId) params.append('userId', userId);
      
      const response = await api.get(`/time-tracking/stats?${params}`);
      
      if (response.data.success) {
        set({ 
          stats: response.data.data,
          loading: false,
          lastUpdate: new Date().toISOString()
        });
        return { success: true, data: response.data.data };
      }
    } catch (error) {
      console.warn('API stats non disponible, calcul local...', error.response?.status);
      
      // FALLBACK vers calcul local
      if (error.response?.status === 404) {
        await get().fetchTimeHistory(days, userId);
        return { success: true, data: get().stats };
      }
      
      set({ 
        error: error.response?.data?.message || error.message,
        loading: false 
      });
      return { success: false, error: error.message };
    }
  },

  // Rapport mensuel
  fetchMonthlyReport: async (month = null, year = null, userId = null) => {
    set({ loading: true, error: null });
    
    try {
      const params = new URLSearchParams();
      if (month) params.append('month', month);
      if (year) params.append('year', year);
      if (userId) params.append('userId', userId);
      
      const response = await api.get(`/time-tracking/report/monthly?${params}`);
      
      if (response.data.success) {
        set({ 
          monthlyReport: response.data.data,
          loading: false
        });
        return { success: true, data: response.data.data };
      }
    } catch (error) {
      set({ 
        error: error.response?.data?.message || error.message,
        loading: false 
      });
      return { success: false, error: error.message };
    }
  },

  // Historique optimisé
  fetchTimeHistory: async (days = 30, userId = null) => {
    set({ loading: true, error: null });
    
    try {
        const today = new Date();
        const endDateObj = new Date(today);
        endDateObj.setDate(today.getDate() + 1);
        const endDate = endDateObj.toISOString().split('T')[0];
        const startDate = new Date(today - days * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];
      
      const params = new URLSearchParams({ startDate, endDate });
      if (userId) params.append('userId', userId);
      
      const response = await api.get(`/time-tracking/range?${params}`);
      
      if (response.data.success) {
        const entries = response.data.data || [];
        const processedData = calculateTotalHours(entries);
        
        set({ 
          timeHistory: entries,
          processedHistory: processedData,
          loading: false,
          lastUpdate: new Date().toISOString(),
          _processedCache: null,
          _cacheKey: null,
          _cacheTime: 0
        });
        
        // Calculs automatiques
        get().calculateStats(entries);
        return { success: true, data: entries };
      } else {
        throw new Error(response.data.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      return { success: false, error: errorMessage };
    }
  },

// Fonction pour récupérer le résumé d'équipe
fetchTeamSummary: async (days = 30, structureId = null) => {
  set({ teamLoading: true, error: null });
  
  try {
    // Utiliser le bon nom de paramètre
    const params = new URLSearchParams();
    
    // Mapper les périodes textuelles vers des nombres de jours
    let daysNumber = 30; // Par défaut
    switch (days) {
      case 'current_week':
      case 'previous_week':
        daysNumber = 7;
        break;
      case 'current_month':
      case 'previous_month':
      case 'last_30_days':
        daysNumber = 30;
        break;
      case 'current_quarter':
      case 'previous_quarter':
        daysNumber = 90;
        break;
      case 'current_year':
      case 'previous_year':
        daysNumber = 365;
        break;
      default:
        daysNumber = parseInt(days) || 30;
    }
    
    params.append('days', daysNumber.toString());
    if (structureId) params.append('structureId', structureId);
    
    console.log('📡 Requête team-summary avec params:', params.toString());
    
    const response = await api.get(`/time-tracking/team-summary?${params}`);
    console.log('📊 Réponse brute team-summary:', response.data);
    
    if (response.data.success) {
      set({ 
        teamSummary: response.data.data,
        teamLoading: false
      });
      return { success: true, data: response.data.data };
    } else {
      throw new Error(response.data.message || 'Erreur lors du chargement');
    }
  } catch (error) {
    console.error('❌ Erreur fetchTeamSummary:', error);
    const errorMessage = error.response?.data?.message || error.message;
    set({ error: errorMessage, teamLoading: false });
    return { success: false, error: errorMessage };
  }
},

// Fonction d'export des données d'équipe
exportTeamData: async (format = 'csv', filters = {}) => {
  try {
    const params = new URLSearchParams(filters);
    params.append('format', format);
    
    const response = await api.get(`/time-tracking/team-export?${params}`, {
      responseType: 'blob'
    });
    
    // Créer le téléchargement
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `team_summary_${new Date().toISOString().split('T')[0]}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    set({ error: errorMessage });
    return { success: false, error: errorMessage };
  }
},

// Enregistrer une entrée de temps
  recordTimeEntry: async (type, taskId = null, comment = '') => {
    set({ error: null });
    
    try {
      const payload = {
        tracking_type: type,
        date_time: new Date().toISOString(),
        validated: false,
        comment: comment.trim()
      };

      if (taskId) payload.task_id = taskId;

      const response = await api.post('/time-tracking', payload);
      
      if (response.data.success) {
        await get().fetchTodayEntries();        
        //  Invalider le cache
        set({
          _processedCache: null,
          _cacheKey: null,
          _cacheTime: 0
        });
        return { success: true, data: response.data.data };
      } else {
        throw new Error(response.data.message || 'Erreur lors du pointage');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

// Mettre à jour une entrée de temps
  updateTimeEntry: async (entryId, updateData) => {
    try {
      const response = await api.put(`/time-tracking/${entryId}`, updateData);
      
      if (response.data.success) {
        await Promise.all([
          get().fetchTodayEntries(),
          get().fetchTimeHistory()
        ]);
        return { success: true, data: response.data.data };
      } else {
        throw new Error(response.data.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },
// Supprimer une entrée de temps
  deleteTimeEntry: async (entryId) => {
    try {
      const response = await api.delete(`/time-tracking/${entryId}`);
      
      if (response.data.success) {
        await Promise.all([
          get().fetchTodayEntries(),
          get().fetchTimeHistory()
        ]);
        return { success: true };
      } else {
        throw new Error(response.data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  // Calculs consolidés (utilise les utils)
  calculateStats: (entries) => {
  const weeklyStats = calculateWeeklyStats(entries);
  const monthlyStats = calculateMonthlyStats(entries);
  
  set({ weeklyStats, monthlyStats });
  },

  // Getters (utilise les utils)
  getTodayStatus: () => getTodayStatus(get().todayEntries),
  
  // getProcessedHistory: () => calculateTotalHours(get().timeHistory),
  getProcessedHistory: () => {
    const { timeHistory, _processedCache, _cacheKey, _cacheTime } = get();
    const currentKey = timeHistory.length > 0 ? 
      `${timeHistory.length}-${timeHistory[0]?.id}-${timeHistory[timeHistory.length-1]?.id || 'new'}` : 
      'empty';
    const now = Date.now();
    
    // Cache simple mais efficace
    if (_cacheKey === currentKey && now - _cacheTime < 2 * 60 * 1000) {
      return _processedCache;
    }
    
    // Calculer et cacher
    const result = calculateTotalHours(timeHistory);
    set({
      _processedCache: result,
      _cacheKey: currentKey,
      _cacheTime: now
    });
    
    return result;
  },
  
  canPerformAction: (actionType) => {
    const todayStatus = get().getTodayStatus();
    
    const rules = {
      arrival: !todayStatus.arrival,
      break_start: todayStatus.arrival && !todayStatus.breakStart && !todayStatus.departure,
      break_end: todayStatus.breakStart && !todayStatus.breakEnd && !todayStatus.departure,
      departure: todayStatus.arrival && !todayStatus.departure && 
                (!todayStatus.breakStart || todayStatus.breakEnd)
    };
    
    return rules[actionType] || false;
  },

  getCurrentWorkingTime: () => {
    const todayStatus = get().getTodayStatus();
    
    if (!todayStatus.arrival) return 0;
    
    const now = new Date();
    const arrivalTime = new Date(todayStatus.arrival.date_time);
    let workingMinutes = (now - arrivalTime) / (1000 * 60);
    
    // Déduire les pauses
    if (todayStatus.breakStart && !todayStatus.breakEnd) {
      const breakStartTime = new Date(todayStatus.breakStart.date_time);
      workingMinutes -= (now - breakStartTime) / (1000 * 60);
    }
    
    if (todayStatus.breakStart && todayStatus.breakEnd) {
      const breakStartTime = new Date(todayStatus.breakStart.date_time);
      const breakEndTime = new Date(todayStatus.breakEnd.date_time);
      workingMinutes -= (breakEndTime - breakStartTime) / (1000 * 60);
    }
    
    return Math.max(0, workingMinutes / 60);
  },

  // Actions utilitaires
  clearError: () => set({ error: null }),

  reset: () => set({
    todayEntries: [],
    timeHistory: [],
    processedHistory: [],
    monthlyReport: {},
    stats: {},
    weeklyStats: {},
    monthlyStats: {},
    loading: false,
    error: null,
    lastUpdate: null,
    _processedCache: null,
    _cacheKey: null,
    _cacheTime: 0
  })

}));

